import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const AdminSidebar = ({ isCollapsed = false, onToggleCollapse }) => {
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState(['content']);

  const menuSections = [
    {
      id: 'content',
      label: 'Content Management',
      items: [
        {
          label: 'Dashboard',
          path: '/admin-dashboard',
          icon: 'LayoutDashboard',
        },
        {
          label: 'Create Newsletter',
          path: '/admin-dashboard/create',
          icon: 'FilePlus',
        },
        {
          label: 'All Newsletters',
          path: '/admin-dashboard/newsletters',
          icon: 'FileText',
        },
        {
          label: 'Drafts',
          path: '/admin-dashboard/drafts',
          icon: 'FileEdit',
          badge: 3,
        },
      ],
    },
    {
      id: 'subscribers',
      label: 'Subscriber Tools',
      items: [
        {
          label: 'Subscribers',
          path: '/admin-dashboard/subscribers',
          icon: 'Users',
        },
        {
          label: 'Email Lists',
          path: '/admin-dashboard/lists',
          icon: 'Mail',
        },
      ],
    },
    {
      id: 'analytics',
      label: 'Analytics',
      items: [
        {
          label: 'Performance',
          path: '/admin-dashboard/analytics',
          icon: 'BarChart3',
        },
        {
          label: 'Reports',
          path: '/admin-dashboard/reports',
          icon: 'FileBarChart',
        },
      ],
    },
  ];

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) =>
      prev?.includes(sectionId)
        ? prev?.filter((id) => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const isSectionExpanded = (sectionId) => {
    return expandedSections?.includes(sectionId);
  };

  return (
    <aside
      className={`fixed lg:sticky top-0 left-0 h-screen bg-card border-r border-border shadow-elevation-sm transition-all duration-300 ease-smooth z-[50] ${
        isCollapsed ? 'w-16' : 'w-60'
      }`}
    >
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between h-[60px] px-4 border-b border-border">
          {!isCollapsed && (
            <Link
              to="/admin-dashboard"
              className="flex items-center gap-2 hover:opacity-80 transition-opacity duration-250"
            >
              <img 
                src="/assets/images/LOGO_THEPS_Main_one_2_-1763819279069.png" 
                alt="THEPS logo featuring yellow circular background with blue handshake illustration surrounded by 12 blue stars"
                className="h-8 w-8 object-contain"
              />
              <span className="text-sm font-heading font-semibold text-foreground">
                Admin Panel
              </span>
            </Link>
          )}
          {isCollapsed && (
            <img 
              src="/assets/images/LOGO_THEPS_Main_one_2_-1763819279069.png" 
              alt="THEPS logo featuring yellow circular background with blue handshake illustration surrounded by 12 blue stars"
              className="h-8 w-8 object-contain mx-auto"
            />
          )}
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className="hidden lg:flex items-center justify-center w-8 h-8 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors duration-250"
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <Icon
                name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'}
                size={18}
              />
            </button>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-2">
          {menuSections?.map((section) => (
            <div key={section?.id} className="mb-6">
              {!isCollapsed && (
                <button
                  onClick={() => toggleSection(section?.id)}
                  className="flex items-center justify-between w-full px-3 py-2 text-xs font-heading font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors duration-250"
                >
                  <span>{section?.label}</span>
                  <Icon
                    name={
                      isSectionExpanded(section?.id)
                        ? 'ChevronDown' :'ChevronRight'
                    }
                    size={14}
                  />
                </button>
              )}

              <div
                className={`space-y-1 transition-all duration-300 ease-smooth ${
                  isCollapsed || isSectionExpanded(section?.id)
                    ? 'max-h-96 opacity-100' :'max-h-0 opacity-0 overflow-hidden'
                }`}
              >
                {section?.items?.map((item) => (
                  <Link
                    key={item?.path}
                    to={item?.path}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-250 group ${
                      isActivePath(item?.path)
                        ? 'bg-primary text-primary-foreground shadow-elevation-sm'
                        : 'text-foreground hover:bg-muted hover:text-primary'
                    }`}
                    title={isCollapsed ? item?.label : ''}
                  >
                    <Icon
                      name={item?.icon}
                      size={18}
                      className={`flex-shrink-0 ${
                        isActivePath(item?.path)
                          ? 'text-primary-foreground'
                          : 'text-muted-foreground group-hover:text-primary'
                      }`}
                    />
                    {!isCollapsed && (
                      <>
                        <span className="text-sm font-medium flex-1">
                          {item?.label}
                        </span>
                        {item?.badge && (
                          <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold bg-accent text-accent-foreground rounded-full">
                            {item?.badge}
                          </span>
                        )}
                      </>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="border-t border-border p-4">
          <Link
            to="/admin-login"
            className={`flex items-center gap-3 px-3 py-2 text-foreground hover:bg-muted hover:text-error rounded-lg transition-colors duration-250 ${
              isCollapsed ? 'justify-center' : ''
            }`}
            title={isCollapsed ? 'Logout' : ''}
          >
            <Icon name="LogOut" size={18} className="text-error" />
            {!isCollapsed && (
              <span className="text-sm font-medium">Logout</span>
            )}
          </Link>
        </div>
      </div>
    </aside>
  );
};

export default AdminSidebar;